import React, { useState } from 'react';
import { Rocket } from 'lucide-react';
import { Button, toast } from '@ncoder/ui';
import { AgentDeploymentContent } from '../../../components/organisms/deployment';

export const AgentDeployment: React.FC = () => {
  const [isDeploying, setIsDeploying] = useState(false);
  const [productionTraffic, setProductionTraffic] = useState('100');
  const [canaryEnabled, setCanaryEnabled] = useState(false);

  const [deployments] = useState([
    {
      id: '1',
      name: 'Production Bot',
      environment: 'production',
      status: 'active' as const,
      version: 'v2.1.0',
      endpoint: 'https://api.company.com/agent',
      traffic: '100%',
      lastDeployed: '2 hours ago',
      uptime: '99.9%',
      requestsPerMin: '127',
    },
    {
      id: '2',
      name: 'Staging Bot',
      environment: 'staging',
      status: 'active' as const,
      version: 'v2.2.0-beta',
      endpoint: 'https://staging-api.company.com/agent',
      traffic: '0%',
      lastDeployed: '1 day ago',
      uptime: '99.5%',
      requestsPerMin: '45',
    },
    {
      id: '3',
      name: 'Development Bot',
      environment: 'development',
      status: 'inactive' as const,
      version: 'v2.3.0-dev',
      endpoint: 'https://dev-api.company.com/agent',
      traffic: '0%',
      lastDeployed: '3 days ago',
      uptime: '98.2%',
      requestsPerMin: '12',
    },
  ]);

  const [versions] = useState([
    {
      version: 'v2.2.0',
      description: 'Latest stable release',
      isCurrent: true,
    },
    {
      version: 'v2.1.0',
      description: 'Previous stable release',
      isCurrent: false,
    },
    {
      version: 'v2.3.0-beta',
      description: 'Beta release',
      isCurrent: false,
    },
  ]);

  const [metrics] = useState({
    uptime: '99.9%',
    requests: '127',
    responseTime: '0.8s',
    errors: '3/hr',
  });

  const [healthChecks] = useState([
    {
      service: 'API Health',
      status: 'healthy' as const,
      responseTime: '45ms',
      lastChecked: '1 min ago',
    },
    {
      service: 'Database',
      status: 'healthy' as const,
      responseTime: '12ms',
      lastChecked: '1 min ago',
    },
    {
      service: 'Cache',
      status: 'healthy' as const,
      responseTime: '8ms',
      lastChecked: '1 min ago',
    },
    {
      service: 'External APIs',
      status: 'warning' as const,
      responseTime: '324ms',
      lastChecked: '2 min ago',
    },
  ]);

  const handleDeploy = () => {
    setIsDeploying(true);
    toast({
      title: 'Deployment Started',
      description: 'Your agent is being deployed...',
    });
    setTimeout(() => {
      setIsDeploying(false);
      toast({
        title: 'Deployment Successful',
        description: 'Your agent has been deployed successfully!',
      });
    }, 2000);
  };

  const handleSettings = (id: string) => {
    toast({
      title: 'Settings',
      description: `Opening settings for deployment ${id}`,
    });
  };

  const handleViewLogs = (id: string) => {
    toast({
      title: 'View Logs',
      description: `Opening logs for deployment ${id}`,
    });
  };

  const handleRollback = (version: string) => {
    toast({
      title: 'Rolling Back',
      description: `Rolling back to version ${version}...`,
    });
  };

  const handleDeployVersion = (version: string) => {
    toast({
      title: 'Deploying Version',
      description: `Deploying version ${version}...`,
    });
  };

  const handleProductionTrafficChange = (value: string) => {
    setProductionTraffic(value);
    toast({
      title: 'Traffic Updated',
      description: `Production traffic set to ${value}%`,
    });
  };

  const handleCanaryEnabledChange = (enabled: boolean) => {
    setCanaryEnabled(enabled);
    toast({
      title: enabled ? 'Canary Enabled' : 'Canary Disabled',
      description: enabled
        ? 'Canary deployment has been enabled'
        : 'Canary deployment has been disabled',
    });
  };

  const handleQuickDeploy = () => {
    toast({
      title: 'Quick Deploy',
      description: 'Starting quick deployment...',
    });
  };

  const handleConfiguration = () => {
    toast({
      title: 'Configuration',
      description: 'Opening deployment configuration...',
    });
  };

  const handleViewLiveAgent = () => {
    toast({
      title: 'View Live Agent',
      description: 'Opening live agent view...',
    });
  };

  return (
    <div className="w-full max-w-none space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-page-title flex items-center gap-2">
            <Rocket className="h-8 w-8 text-primary" />
            Agent Deployment
          </h1>
          <p className="text-subtitle">Deploy and manage your agent across environments</p>
        </div>
        <Button size="sm" disabled={isDeploying} onClick={handleDeploy}>
          <Rocket className="h-4 w-4 mr-2" />
          {isDeploying ? 'Deploying...' : 'Deploy Agent'}
        </Button>
      </div>

      {/* Content */}
      <AgentDeploymentContent
        deployments={deployments}
        versions={versions}
        metrics={metrics}
        healthChecks={healthChecks}
        productionTraffic={productionTraffic}
        canaryEnabled={canaryEnabled}
        activeDeployments={2}
        totalEnvironments={3}
        currentVersion="v2.2.0"
        onSettings={handleSettings}
        onViewLogs={handleViewLogs}
        onRollback={handleRollback}
        onDeploy={handleDeployVersion}
        onProductionTrafficChange={handleProductionTrafficChange}
        onCanaryEnabledChange={handleCanaryEnabledChange}
        onQuickDeploy={handleQuickDeploy}
        onConfiguration={handleConfiguration}
        onViewLiveAgent={handleViewLiveAgent}
      />
    </div>
  );
};

export default AgentDeployment;